/**
 * #39 달력 앱에 알림 넣기 — RFC 5545 iCalendar files the user saves into their
 * own calendar (iOS·Google). Bodies never carry amounts or merchant names that
 * the user typed, only the reminder title they chose and a link to record.
 * Reference: https://datatracker.ietf.org/doc/html/rfc5545 (3.6.1 VEVENT,
 * 3.6.6 VALARM, 3.3.10 RECUR, 3.1 line folding).
 */

export type IcsRepeat =
	| { kind: "monthly"; dayOfMonth: number }
	| { kind: "weekly"; weekday: "SU" | "MO" | "TU" | "WE" | "TH" | "FR" | "SA" };

export interface IcsReminder {
	uid: string;
	title: string;
	description: string;
	/** First occurrence, local KST date "YYYY-MM-DD". */
	startDate: string;
	/** Local KST time "HH:MM". */
	time: string;
	repeat: IcsRepeat;
	url?: string;
}

const CRLF = "\r\n";

function escapeText(value: string): string {
	return value
		.replace(/\\/g, "\\\\")
		.replace(/;/g, "\\;")
		.replace(/,/g, "\\,")
		.replace(/\r?\n/g, "\\n");
}

/** Folds a content line at 75 octets without splitting a UTF-8 character. */
export function foldIcsLine(line: string): string {
	const encoder = new TextEncoder();
	const parts: string[] = [];
	let current = "";
	let size = 0;
	for (const char of line) {
		const bytes = encoder.encode(char).length;
		const limit = parts.length === 0 ? 75 : 74;
		if (size + bytes > limit) {
			parts.push(current);
			current = "";
			size = 0;
		}
		current += char;
		size += bytes;
	}
	parts.push(current);
	return parts.join(`${CRLF} `);
}

function rrule(repeat: IcsRepeat): string {
	if (repeat.kind === "weekly") return `FREQ=WEEKLY;BYDAY=${repeat.weekday}`;
	const day = Math.min(31, Math.max(1, Math.round(repeat.dayOfMonth)));
	// Day 29~31 falls back to the month's last day, matching recurringPaymentDate.
	return day >= 29
		? `FREQ=MONTHLY;BYMONTHDAY=${day},-1;BYSETPOS=1`
		: `FREQ=MONTHLY;BYMONTHDAY=${day}`;
}

function stamp(now: Date): string {
	return now
		.toISOString()
		.replace(/[-:]/g, "")
		.replace(/\.\d{3}Z$/, "Z");
}

export function buildIcs(reminders: IcsReminder[], now = new Date()): string {
	const lines = [
		"BEGIN:VCALENDAR",
		"VERSION:2.0",
		"PRODID:-//MUNOPS//Sobidogam//KO",
		"CALSCALE:GREGORIAN",
		"METHOD:PUBLISH",
		"BEGIN:VTIMEZONE",
		"TZID:Asia/Seoul",
		"BEGIN:STANDARD",
		"DTSTART:19880101T000000",
		"TZOFFSETFROM:+0900",
		"TZOFFSETTO:+0900",
		"TZNAME:KST",
		"END:STANDARD",
		"END:VTIMEZONE",
	];
	for (const reminder of reminders) {
		const date = reminder.startDate.replace(/-/g, "");
		const time = `${reminder.time.replace(":", "")}00`;
		lines.push(
			"BEGIN:VEVENT",
			`UID:${reminder.uid}@sobidogam.com`,
			`DTSTAMP:${stamp(now)}`,
			`DTSTART;TZID=Asia/Seoul:${date}T${time}`,
			"DURATION:PT15M",
			`RRULE:${rrule(reminder.repeat)}`,
			`SUMMARY:${escapeText(reminder.title)}`,
			`DESCRIPTION:${escapeText(reminder.description)}`,
			...(reminder.url ? [`URL:${reminder.url}`] : []),
			"BEGIN:VALARM",
			"ACTION:DISPLAY",
			`DESCRIPTION:${escapeText(reminder.title)}`,
			"TRIGGER:PT0M",
			"END:VALARM",
			"END:VEVENT",
		);
	}
	lines.push("END:VCALENDAR");
	return lines.map(foldIcsLine).join(CRLF) + CRLF;
}

/** Next KST date (YYYY-MM-DD) on or after `from` matching the repeat rule. */
export function firstOccurrence(repeat: IcsRepeat, from = new Date()): string {
	const kst = new Date(from.getTime() + 9 * 3_600_000);
	const year = kst.getUTCFullYear();
	const month = kst.getUTCMonth();
	const today = kst.getUTCDate();
	const pad = (value: number) => String(value).padStart(2, "0");
	if (repeat.kind === "weekly") {
		const order = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
		const target = order.indexOf(repeat.weekday);
		const offset = (target - kst.getUTCDay() + 7) % 7;
		const next = new Date(Date.UTC(year, month, today + offset));
		return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
	}
	const clamp = (y: number, m: number) =>
		Math.min(repeat.dayOfMonth, new Date(Date.UTC(y, m + 1, 0)).getUTCDate());
	let y = year;
	let m = month;
	if (clamp(y, m) < today) {
		m += 1;
		if (m > 11) {
			m = 0;
			y += 1;
		}
	}
	return `${y}-${pad(m + 1)}-${pad(clamp(y, m))}`;
}
